import {
  Box,
  IconButton,
  ListItemButton,
  Stack,
  SvgIcon,
  Theme,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/system/colorManipulator";
import TopNavSection from "./TopNavSection";
import MenuIcon from "@mui/icons-material/Menu";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { usePageHeader } from "global/components/PageHeader/PageHeaderContext";
import { useAppSelector } from "redux/hooks";
import { selectSideNavCollpased } from "redux/preferencesSlice";
import { SIDE_NAV_COLLAPSED_WIDTH, SIDE_NAV_WIDTH } from "../DrawerLayout";
import { usePopover } from "hooks/use-popover";
import viewpaths from "global/constants/ViewPathConstants";
import CustomPopper from "global/components/CustomPopper/CustomPopper";
import urls from "global/constants/UrlConstants";
import usageIcon from "../../../assets/icons/TopNavIcons/usageIcon.svg";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import {
  HelpCenterTileIcon,
  HelpNavIcon,
  TicketTileIcon,
} from "utils/TopNavIcons";
import { panel, signer } from "theme/colors";
import { topNavDividerSx, topNavIconButtonSx } from "./TopNav.styles";
import PlanUsage from "./PlanUsage";
import Notification from "global/common/Notifications/Notification";
import { formatCount, isOfferActive, isUnlimitedLimit } from "helpers/methods";
import UnlimitedLabel from "global/components/UnlimitedLabel/UnlimitedLabel";
import { hasAccessTo } from "layouts/Config";
import strings from "global/constants/StringConstants";
import Inbox from "global/common/Inbox/Inbox";
import { selectAccount } from "redux/authSlice";
import { useEffect, useState } from "react";
import { fetchAccountDetails, fetchCurrentPlan } from "Services/CommonServices";
import { TourLauncher } from "tour";

const TOP_NAV_HEIGHT = 64;

// The Help & support panel rows. Each carries its own tile hue so the list
// reads as a set of destinations rather than a plain menu.
const menuItems = [
  {
    href: urls.PROD
      ? "https://inkfree.app/help/"
      : "https://uat.inkfree.app/help/",
    icon: <HelpCenterTileIcon sx={{ fontSize: 20 }} />,
    label: "Help Center",
    description: "Guides, FAQs and how-tos",
    tileBg: signer.orange.tint,
    tileColor: signer.orange.main,
    external: true,
    condition: true,
  },
  {
    href: `${viewpaths.ticketsViewPath}`,
    icon: <TicketTileIcon sx={{ fontSize: 20 }} />,
    label: "Support Tickets",
    description: "Track or raise a request",
    tileBg: signer.purple.tint,
    tileColor: signer.purple.main,
    external: false,
    condition: true,
  },
];

interface CustomProps {
  onMobileNavOpen: () => void;
  sections?: any;
}

const TopNav = (props: CustomProps) => {
  const theme = useTheme();
  const lgUp = useMediaQuery((theme: Theme) => theme.breakpoints.up("lg"));
  const smUp = useMediaQuery((theme: Theme) => theme.breakpoints.up("sm"));
  const sideNavCollpased = useAppSelector(selectSideNavCollpased);
  const accountName = useAppSelector(selectAccount);
  const popover = usePopover<any>();
  const usagePopover = usePopover<any>();
  const pageHeader = usePageHeader();

  const [accountDetails, setAccountDetails] = useState<any>({});
  const [currentPlan, setCurrentPlan] = useState<any>({});

  const hasBillingFetchPermission = hasAccessTo(
    strings.resources.billing,
    strings.fetchPermission,
  );

  useEffect(() => {
    if (hasBillingFetchPermission && accountName) {
      Promise.all([fetchCurrentPlan(accountName), fetchAccountDetails()]).then(
        ([planRes, detailsRes]) => {
          setCurrentPlan(planRes);
          setAccountDetails(detailsRes);
        },
      );
    }
  }, [hasBillingFetchPermission, accountName]);

  const CertifiedSignedStats = accountDetails?.appPlanLimitList?.find(
    (plan: any) => plan.entity === strings.notariusVerifiedEnvelope,
  );

  const certifiedUsed = CertifiedSignedStats?.current ?? 0;
  const certifiedLimit = CertifiedSignedStats?.limit ?? 0;

  const getMenuSection = () => {
    return (
      <Stack>
        {/* Header — same 17px/700 title bar as the Inbox and Notifications
            panels, so the three top-nav popovers read as one family. */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.25}
          sx={{
            px: "20px",
            py: "16px",
            borderBottom: `1px solid ${panel.border}`,
          }}
        >
          <Typography
            component="h2"
            sx={{ fontSize: "17px", fontWeight: 700, color: panel.title }}
          >
            Help & support
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <IconButton
            aria-label="Close help"
            onClick={() => popover.handleClose()}
            sx={{
              color: panel.metaText,
              p: "4px",
              "&:hover": { backgroundColor: theme.palette.signer.surface.muted },
            }}
          >
            <CloseRoundedIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Stack>

        <Box sx={{ py: "8px" }}>
          {menuItems.map(
            (item, index) =>
              item.condition && (
                <ListItemButton
                  key={index}
                  href={item.href}
                  onClick={popover.handleClose}
                  LinkComponent="a"
                  target={item.external ? "_blank" : "_self"}
                  rel="noopener noreferrer"
                  sx={{
                    px: "20px",
                    py: "12px",
                    gap: "12px",
                    alignItems: "center",
                    "&:hover": { backgroundColor: panel.hover },
                    "&.Mui-focusVisible": {
                      outline: (theme) =>
                        `2px solid ${theme.palette.primary.main}`,
                      outlineOffset: "-2px",
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "12px",
                      flexShrink: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: item.tileBg,
                      color: item.tileColor,
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 700,
                        lineHeight: 1.35,
                        color: panel.title,
                      }}
                    >
                      {item.label}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "12px",
                        fontWeight: 500,
                        color: panel.metaText,
                      }}
                    >
                      {item.description}
                    </Typography>
                  </Stack>
                </ListItemButton>
              ),
          )}
          <TourLauncher onLaunch={popover.handleClose} />
        </Box>
      </Stack>
    );
  };

  return (
    <Box
      component="header"
      sx={{
        backdropFilter: "blur(6px)",
        backgroundColor: (theme) =>
          alpha(theme.palette.background.default, 0.8),
        position: "sticky",
        left: {
          lg: `${
            sideNavCollpased ? SIDE_NAV_COLLAPSED_WIDTH : SIDE_NAV_WIDTH
          }px`,
        },
        // top: isOfferActive() ? 30 : 0,
        top: {
          lg: isOfferActive() ? "30px" : "0px",
          md: isOfferActive() ? "30px" : "0px",
          sm: isOfferActive() ? "48px" : "0px",
          xs: isOfferActive() ? "48px" : "0px",
        },
        width: {
          lg: `calc(100% - ${
            sideNavCollpased ? SIDE_NAV_COLLAPSED_WIDTH : SIDE_NAV_WIDTH
          }px)`,
        },
        zIndex: (theme) => theme.zIndex.appBar,
        borderBottom: "1px solid rgba(117, 81, 208, 0.1)",
      }}
    >
      <Stack
        alignItems="center"
        direction="row"
        justifyContent="space-between"
        spacing={2}
        sx={{
          minHeight: TOP_NAV_HEIGHT,
          px: 2,
        }}
      >
        {/*
          `minWidth: 0` lets this group shrink so a long screen name (a folder
          the user has drilled into, say) truncates instead of pushing the bar
          past a 375px viewport. Only the title gives way: both buttons below
          carry `flexShrink: 0`, so the group cannot collapse onto the actions.
        */}
        <Stack
          alignItems="center"
          direction="row"
          spacing={1}
          sx={{ minWidth: 0 }}
        >
          {!lgUp &&
            /*
             * Screens opt into an app-bar title (and an "up one level" arrow)
             * through `useSetPageHeader`; anything that registers nothing keeps
             * the plain menu button, exactly as before.
             */
            (pageHeader.onBack ? (
              <IconButton
                onClick={pageHeader.onBack}
                aria-label="Back"
                sx={{ flexShrink: 0, color: "text.primary" }}
              >
                <SvgIcon>
                  <ArrowBackIcon />
                </SvgIcon>
              </IconButton>
            ) : (
              <IconButton
                onClick={props.onMobileNavOpen}
                aria-label="Open navigation menu"
                aria-haspopup="dialog"
                sx={{ flexShrink: 0 }}
              >
                <SvgIcon>
                  <MenuIcon />
                </SvgIcon>
              </IconButton>
            ))}
          {!lgUp && !!pageHeader.title && (
            <Typography
              component="h2"
              sx={{
                minWidth: 0,
                fontSize: { xs: "16px", sm: "16.5px" },
                fontWeight: 800,
                color: "text.primary",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {pageHeader.title}
            </Typography>
          )}
        </Stack>
        {/*
          The phone references draw the app bar as menu · screen name · badge
          icon · avatar. Usage & plan and Help — both also reachable from the
          drawer and the account menu — are dropped below `sm` and the groups
          sit tighter, so the bar fits a 375px viewport instead of running ~150px
          past it and scrolling every screen sideways.
        */}
        <Stack
          alignItems="center"
          direction="row"
          spacing={{ xs: 1, sm: 3 }}
          sx={{ flexShrink: 0 }}
        >
          {/* A screen's own header action, registered through
              `useSetPageHeader`. It leads the group so it keeps the reference's
              "screen name … action" reading, and is inert for every screen that
              registers nothing. */}
          {!lgUp && !!pageHeader.actions && (
            <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
              {pageHeader.actions}
            </Box>
          )}
          {hasAccessTo(strings.resources.billing, strings.fetchPermission) && (
            <Stack
              component="button"
              type="button"
              direction="row"
              spacing={0}
              alignItems="center"
              aria-haspopup="dialog"
              aria-expanded={usagePopover.open}
              // WCAG 2.5.3: from lg up the button shows "Usage & Plan" plus the
              // plan line, so that visible text is its name; below lg it is
              // icon-only and needs the label.
              aria-label={lgUp ? undefined : "Usage & Plan"}
              sx={{
                display: { xs: "none", sm: "flex" },
                cursor: "pointer",
                border: "none",
                background: "transparent",
                p: 0,
                font: "inherit",
                color: "inherit",
                textAlign: "left",
                "&:focus-visible": {
                  outline: (theme) => `2px solid ${theme.palette.primary.main}`,
                  outlineOffset: "2px",
                  borderRadius: "8px",
                },
              }}
              onClick={usagePopover.handleOpen}
              ref={usagePopover.anchorRef}
            >
              <img
                src={usageIcon}
                height={"auto"}
                width={"auto"}
                alt=""
                aria-hidden="true"
              />
              <Box
                component="span"
                ml={1}
                display="flex"
                flexDirection="column"
              >
                {lgUp && (
                  <Typography component="span" variant="body2" fontWeight={600}>
                    {"Usage & Plan"}
                  </Typography>
                )}
                {lgUp && (
                  <Typography
                    component="span"
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: "0.7rem", lineHeight: 1 }}
                  >
                    {isUnlimitedLimit(certifiedLimit) ? (
                      <>
                        {formatCount(certifiedUsed)} docs this month ·{" "}
                        <UnlimitedLabel iconSize={12} />
                      </>
                    ) : (
                      `${formatCount(certifiedUsed)} / ${formatCount(certifiedLimit)} docs`
                    )}
                  </Typography>
                )}
              </Box>
            </Stack>
          )}
          <Box sx={{ ...topNavDividerSx, display: { xs: "none", sm: "block" } }} />
          {/* The three icon buttons sit together as one group, so they get a
              tighter gap than the rules and blocks around them. */}
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Inbox />
            <Notification />
            <Tooltip title="Help" placement="bottom" arrow>
              <Box
                component="button"
                type="button"
                aria-label="Help and support"
                aria-haspopup="dialog"
                aria-expanded={popover.open}
                sx={{
                  ...topNavIconButtonSx,
                  display: { xs: "none", sm: "flex" },
                }}
                onClick={popover.handleOpen}
                ref={popover.anchorRef}
              >
                <HelpNavIcon sx={{ fontSize: 20 }} />
              </Box>
            </Tooltip>
          </Stack>
          <Box sx={{ ...topNavDividerSx, display: { xs: "none", sm: "block" } }} />
          <TopNavSection />
        </Stack>
      </Stack>
      <CustomPopper
        open={popover.open}
        anchorRef={popover.anchorRef.current}
        handleClose={popover.handleClose}
        children={getMenuSection()}
        ariaLabel="Help and support"
        width="20rem"
        // Header and rows run edge to edge, so the panel carries no padding.
        padding={0}
        shadow={panel.shadow}
      />
      <CustomPopper
        open={usagePopover.open}
        anchorRef={usagePopover.anchorRef.current}
        handleClose={usagePopover.handleClose}
        children={<PlanUsage />}
        ariaLabel="Usage and plan"
        width="fit-content"
        padding={0}
      />
    </Box>
  );
};

export default TopNav;
